import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useRef, useState } from 'react'
import {
  useNavigate,
  useParams,
  useSearchParams,
  Link as RouterLink,
} from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ImagePlus, Library, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AdminFormPage } from '../components/AdminFormPage'
import { adminApplicationsService } from '../services/admin.applications.service'
import { adminSubmissionsService } from '../services/admin.submissions.service'
import { useProductions, ProvisionProductionDialog } from '../productions'
import { ExternalLink, Sparkles } from 'lucide-react'
import { ref, uploadBytesResumable, getDownloadURL } from '@/lib/firebase/storage'
import { storage } from '@/lib/firebase/storage'
import { setApplicationDeadline } from '@/lib/firebase/functions'
import { MediaLibraryPickerDialog } from '../components/media/MediaLibraryPickerDialog'
import { FocusPointPicker } from '@/features/designer/components/FocusPointPicker'
import type { ApplicationFormValues } from '../types'
import type { ProductionDocument } from '@/types'
import { adminProductionsService } from '../productions/services/adminProductions.service'
import { linkedIntakeApplicationDefaults } from '../productions/lib/linkedCreationDefaults'

const schema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-z0-9-]+$/, 'Slug may only contain lowercase letters, numbers, and hyphens'),
  status: z.enum(['draft', 'open', 'closed']),
  isPublished: z.boolean(),
  autoAccept: z.boolean(),
  order: z.number().int().min(0),
  deadline: z.string(), // ISO datetime string or empty
  applicationType: z.enum(['production_intake', 'tech_questionnaire', 'general']),
  productionId: z.string(),
})

type FormValues = z.infer<typeof schema>

export function AdminApplicationEditPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const requestedProductionId = isNew ? (searchParams.get('productionId')?.trim() ?? '') : ''

  const [loading, setLoading] = useState(!isNew || Boolean(requestedProductionId))
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [linkedProduction, setLinkedProduction] = useState<
    (ProductionDocument & { id: string }) | null
  >(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageFocus, setImageFocus] = useState<{ x: number; y: number }>({ x: 0.5, y: 0.5 })
  const [imageUploading, setImageUploading] = useState(false)
  const [imageUploadProgress, setImageUploadProgress] = useState(0)
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false)
  const [prevDeadline, setPrevDeadline] = useState<string>('')
  const [typeLocked, setTypeLocked] = useState(false)
  const { productions } = useProductions()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const createdIdRef = useRef<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      description: '',
      slug: '',
      status: 'draft',
      isPublished: false,
      autoAccept: false,
      order: 0,
      deadline: '',
      applicationType: 'general',
      productionId: '',
    },
  })

  const watchedTitle = watch('title')
  const watchedApplicationType = watch('applicationType')
  const watchedProductionId = watch('productionId')
  const [provisionOpen, setProvisionOpen] = useState(false)

  // Auto-slug from title (only when creating new)
  useEffect(() => {
    if (!isNew) return
    setValue('slug', adminApplicationsService.slugify(watchedTitle), { shouldValidate: false })
  }, [watchedTitle, isNew, setValue])

  useEffect(() => {
    if (isNew || !id) return
    setLoading(true)
    adminApplicationsService
      .getById(id)
      .then(async (app) => {
        if (!app) return
        const deadline =
          app.deadline
            ? toDatetimeLocal(
                (app.deadline as unknown as { toDate(): Date }).toDate?.() ??
                  new Date(app.deadline as unknown as string)
              )
            : ''
        setPrevDeadline(deadline)
        setImageUrl(app.imageUrl ?? null)
        setImageFocus({ x: app.imageFocusX ?? 0.5, y: app.imageFocusY ?? 0.5 })
        reset({
          title: app.title,
          description: app.description,
          slug: app.slug,
          status: app.status,
          isPublished: app.isPublished,
          autoAccept: app.autoAccept ?? false,
          order: app.order,
          deadline,
          applicationType: app.applicationType ?? 'general',
          productionId: app.productionId ?? '',
        })
        if (app.productionId) {
          const production = await adminProductionsService.getById(app.productionId)
          if (production) setLinkedProduction({ ...production, id: app.productionId })
        }
      })
      .catch((err: unknown) =>
        setSubmitError(formatUserFacingError(err, 'Failed to load application'))
      )
      .finally(() => setLoading(false))

    // Determine whether the type field should be locked. Locking is enforced
    // by the service layer; this is just UI affordance.
    adminSubmissionsService
      .getByApplicationId(id)
      .then((subs) => setTypeLocked(subs.length > 0))
      .catch(() => {
        // Non-fatal: leave unlocked, service layer still guards the write.
      })
  }, [id, isNew, reset])

  // Creating from a production inherits the shared identity and linkage once.
  // The standalone new-application route keeps the complete blank form.
  useEffect(() => {
    if (!isNew || !requestedProductionId) return
    setLoading(true)
    setSubmitError(null)
    adminProductionsService
      .getById(requestedProductionId)
      .then((production) => {
        if (!production) {
          setSubmitError(withErrorCode('The linked production could not be found. Return to the production and try again.', 'app/validation'))
          return
        }
        const linked = { ...production, id: requestedProductionId }
        const inherited = linkedIntakeApplicationDefaults(linked)
        setLinkedProduction(linked)
        reset({
          ...inherited,
          description: '',
          autoAccept: false,
          order: 0,
          deadline: '',
        })
      })
      .catch((err: unknown) =>
        setSubmitError(formatUserFacingError(err, 'Failed to load linked production')),
      )
      .finally(() => setLoading(false))
  }, [isNew, requestedProductionId, reset])

  async function handleLibrarySelect(url: string) {
    setImageUrl(url)
    setImageFocus({ x: 0.5, y: 0.5 })
    if (id && !isNew) {
      await adminApplicationsService.updateImageUrl(id, url)
      await adminApplicationsService.updateImageFocus(id, 0.5, 0.5)
    }
  }

  function handleFocusChange(x: number, y: number) {
    setImageFocus({ x, y })
    if (id && !isNew) {
      void adminApplicationsService.updateImageFocus(id, x, y)
    }
  }

  async function handleImageUpload(file: File) {
    const applicationId = id && !isNew ? id : (createdIdRef.current ?? 'pending')
    setImageUploading(true)
    setImageUploadProgress(0)
    try {
      const path = `applications/${applicationId}/card-image/${file.name}`
      const storageRef = ref(storage, path)
      await new Promise<void>((resolve, reject) => {
        const task = uploadBytesResumable(storageRef, file)
        task.on(
          'state_changed',
          (snap) =>
            setImageUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          async () => {
            const url = await getDownloadURL(task.snapshot.ref)
            setImageUrl(url)
            setImageFocus({ x: 0.5, y: 0.5 })
            if (id && !isNew) {
              await adminApplicationsService.updateImageUrl(id, url)
              await adminApplicationsService.updateImageFocus(id, 0.5, 0.5)
            }
            resolve()
          }
        )
      })
    } catch {
      setSubmitError(withErrorCode('Image upload failed. Please try again.', 'app/validation'))
    } finally {
      setImageUploading(false)
    }
  }

  async function onSubmit(values: FormValues) {
    setSubmitError(null)
    try {
      const data: ApplicationFormValues = {
        ...values,
        order: Number(values.order),
      }

      let applicationId = id && !isNew ? id : null

      if (isNew) {
        applicationId = await adminApplicationsService.create(data)
        createdIdRef.current = applicationId
        if (applicationId && imageUrl) {
          await adminApplicationsService.updateImageUrl(applicationId, imageUrl)
          await adminApplicationsService.updateImageFocus(applicationId, imageFocus.x, imageFocus.y)
        }
      } else if (applicationId) {
        await adminApplicationsService.update(applicationId, data)
      }

      // Sync deadline via Cloud Tasks if it changed
      if (applicationId && values.deadline !== prevDeadline) {
        try {
          await setApplicationDeadline({
            applicationId,
            deadline: values.deadline || null,
          })
        } catch {
          // Non-fatal: deadline task scheduling failed; log but don't block navigation
          console.warn('Deadline task scheduling failed — application saved without auto-close.')
        }
      }

      navigate(
        requestedProductionId && applicationId
          ? `/admin/applications/${applicationId}/form`
          : '/admin/applications',
      )
    } catch (err) {
      setSubmitError(formatUserFacingError(err, 'Failed to save application'))
    }
  }

  return (
    <AdminFormPage
      title={isNew ? 'New Application' : 'Edit Application'}
      cardTitle={isNew ? 'Create a new application' : 'Update application details'}
      backTo={linkedProduction ? `/admin/productions/${linkedProduction.id}` : '/admin/applications'}
      loading={loading}
      cardSkeletonClassName="h-[600px]"
    >
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-5">
        {requestedProductionId && (
          <>
            <input type="hidden" {...register('title')} />
            <input type="hidden" {...register('slug')} />
            <input type="hidden" {...register('applicationType')} />
            <input type="hidden" {...register('productionId')} />
          </>
        )}
        {linkedProduction && (
          <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">
            <p className="font-medium">Intake application for {linkedProduction.name}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {requestedProductionId
                ? 'The title, slug, application type, and production link were inherited. Add the application-specific description and deadline; after creation, you will continue directly to the form builder.'
                : 'This production uses the application for artist intake and curation.'}
            </p>
            <Button asChild type="button" variant="link" size="sm" className="h-auto px-0 pt-2">
              <RouterLink to={`/admin/productions/${linkedProduction.id}`}>
                View production
              </RouterLink>
            </Button>
          </div>
        )}
        {/* Card Image */}
        <div className="space-y-2">
          <Label>Card Image</Label>
          {imageUrl && !imageUploading && (
            <FocusPointPicker
              imageUrl={imageUrl}
              focusX={imageFocus.x}
              focusY={imageFocus.y}
              onChange={handleFocusChange}
            />
          )}
          <div
            className="relative flex h-40 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-muted-foreground/30 bg-muted/30 transition-colors hover:border-muted-foreground/60"
            onClick={() => fileInputRef.current?.click()}
            hidden={!!imageUrl && !imageUploading}
          >
            {imageUploading ? (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="text-sm">{imageUploadProgress}%</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <ImagePlus className="h-8 w-8" />
                <span className="text-sm">Click to upload image</span>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void handleImageUpload(file)
            }}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <ImagePlus className="h-4 w-4 mr-1.5" />
              {imageUrl ? 'Replace Image' : 'Upload Image'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setLibraryPickerOpen(true)}
            >
              <Library className="h-4 w-4 mr-1.5" />
              Select from Library
            </Button>
          </div>
        </div>

        <MediaLibraryPickerDialog
          open={libraryPickerOpen}
          onClose={() => setLibraryPickerOpen(false)}
          onSelect={(url) => void handleLibrarySelect(url)}
        />

        {!requestedProductionId && (
          <>
            {/* Application Type */}
            <div className="space-y-2">
              <Label>Application Type</Label>
              <Controller
                name="applicationType"
                control={control}
                render={({ field }) => (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <ApplicationTypeCard
                      value="general"
                      label="General"
                      description="Standard application review. No automation. Use for grants, partnerships, and one-off opportunities."
                      selected={field.value === 'general'}
                      disabled={typeLocked}
                      onSelect={() => field.onChange('general')}
                    />
                    <ApplicationTypeCard
                      value="production_intake"
                      label="Production Intake"
                      description="Artists applying to participate in an ODD-hosted production. Enables the curation workspace and acceptance pipeline."
                      selected={field.value === 'production_intake'}
                      disabled={typeLocked}
                      onSelect={() => field.onChange('production_intake')}
                    />
                    <ApplicationTypeCard
                      value="tech_questionnaire"
                      label="Tech Questionnaire"
                      description="Technical info collected from accepted artists. Never appears in the public /applications list — only renders inline after acceptance."
                      selected={field.value === 'tech_questionnaire'}
                      disabled={typeLocked}
                      onSelect={() => field.onChange('tech_questionnaire')}
                    />
                  </div>
                )}
              />
              {typeLocked && (
                <p className="text-xs text-muted-foreground">
                  The application type is locked because submissions already exist for this
                  application.
                </p>
              )}
            </div>

            {/* Link to Production (only for production_intake) */}
            {watchedApplicationType === 'production_intake' && (
              <div className="space-y-1">
                <Label htmlFor="productionId">Link to Production</Label>
                <Controller
                  name="productionId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || '__none__'}
                      onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a production…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {productions.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <p className="text-xs text-muted-foreground">
                  Submissions to this application will be tagged with the linked production for
                  curation routing.
                </p>
                <div className="pt-1">
                  {watchedProductionId ? (
                    <Button asChild type="button" variant="outline" size="sm">
                      <RouterLink to={`/admin/productions/${watchedProductionId}`}>
                        View Production
                        <ExternalLink className="ml-1 h-3 w-3" />
                      </RouterLink>
                    </Button>
                  ) : isNew || !id ? (
                    <p className="text-xs italic text-muted-foreground">
                      Save the application first to provision a production from it.
                    </p>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setProvisionOpen(true)}
                    >
                      <Sparkles className="mr-1 h-3.5 w-3.5" />
                      Provision Production from this Application
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {!isNew && id && (
          <ProvisionProductionDialog
            open={provisionOpen}
            onOpenChange={setProvisionOpen}
            applicationId={id}
            applicationTitle={watchedTitle || 'Untitled Application'}
            onProvisioned={(productionId) => setValue('productionId', productionId)}
          />
        )}

        {/* Title */}
        {!requestedProductionId && (
          <div className="space-y-1">
            <Label htmlFor="title">Title</Label>
            <Input id="title" placeholder="Summer Dance Internship" {...register('title')} />
            {errors.title && <p className="text-sm text-destructive">{withErrorCode(String(errors.title.message), 'app/validation')}</p>}
          </div>
        )}

        {/* Description */}
        <div className="space-y-1">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            rows={3}
            placeholder="Briefly describe this application opportunity…"
            {...register('description')}
          />
          {errors.description && (
            <p className="text-sm text-destructive">{withErrorCode(String(errors.description.message), 'app/validation')}</p>
          )}
        </div>

        {/* Slug */}
        {!requestedProductionId && (
          <div className="space-y-1">
            <Label htmlFor="slug">URL Slug</Label>
            <Input
              id="slug"
              placeholder="summer-dance-internship"
              {...register('slug')}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Public URL: /applications/
              <span className="font-medium">{watch('slug') || '…'}</span>
            </p>
            {errors.slug && <p className="text-sm text-destructive">{withErrorCode(String(errors.slug.message), 'app/validation')}</p>}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          {/* Status */}
          <div className="space-y-1">
            <Label>Status</Label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {/* Order */}
          <div className="space-y-1">
            <Label htmlFor="order">Sort Order</Label>
            <Input
              id="order"
              type="number"
              min={0}
              {...register('order', { valueAsNumber: true })}
            />
            {errors.order && <p className="text-sm text-destructive">{withErrorCode(String(errors.order.message), 'app/validation')}</p>}
          </div>
        </div>

        {/* Deadline */}
        <div className="space-y-1">
          <Label htmlFor="deadline">Deadline (optional)</Label>
          <Input id="deadline" type="datetime-local" {...register('deadline')} />
          <p className="text-xs text-muted-foreground">
            When the deadline passes, the application will automatically close. A countdown will
            appear within 24 hours of the deadline.
          </p>
        </div>

        {/* Published */}
        <div className="flex items-center gap-3">
          <input
            id="isPublished"
            type="checkbox"
            className="h-4 w-4 accent-primary"
            {...register('isPublished')}
          />
          <Label htmlFor="isPublished">Show on /applications listing page</Label>
        </div>

        {/* Auto-accept */}
        <div className="flex items-start gap-3">
          <input
            id="autoAccept"
            type="checkbox"
            className="mt-1 h-4 w-4 accent-primary"
            {...register('autoAccept')}
          />
          <div className="space-y-0.5">
            <Label htmlFor="autoAccept">Auto-accept submissions</Label>
            <p className="text-xs text-muted-foreground">
              When enabled, submissions skip review and are marked accepted. For paid applications, acceptance only occurs after payment succeeds.
            </p>
          </div>
        </div>

        {submitError && <p className="text-sm text-destructive">{submitError}</p>}

        <div className="flex gap-3 pt-2">
          <Button
            type="submit"
            disabled={
              isSubmitting ||
              imageUploading ||
              Boolean(requestedProductionId && !linkedProduction)
            }
          >
            {isSubmitting ? 'Saving…' : isNew ? 'Create Application' : 'Save Changes'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              navigate(
                linkedProduction
                  ? `/admin/productions/${linkedProduction.id}`
                  : '/admin/applications',
              )
            }
          >
            Cancel
          </Button>
        </div>
      </form>
    </AdminFormPage>
  )
}

function toDatetimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

interface ApplicationTypeCardProps {
  value: string
  label: string
  description: string
  selected: boolean
  disabled: boolean
  onSelect: () => void
}

function ApplicationTypeCard({
  label,
  description,
  selected,
  disabled,
  onSelect,
}: ApplicationTypeCardProps) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onSelect}
      disabled={disabled}
      className={`flex flex-col items-start gap-1 rounded-md border p-3 text-left transition-colors ${
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/40'
          : 'border-border hover:bg-muted/40'
      } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
    >
      <span className="text-sm font-semibold">{label}</span>
      <span className="text-xs text-muted-foreground">{description}</span>
    </button>
  )
}
